/**
 * Auth token persistence utilities
 * Handles localStorage-based token storage for immediate sync access
 */

const AUTH_TOKEN_KEY = "bf_auth_token"
// While an admin is impersonating another user, their own (admin) token is
// stashed here so "End Session" can restore it. Its presence is what marks an
// active impersonation session.
const IMPERSONATOR_TOKEN_KEY = "bf_impersonator_token"
// Where to send the impersonator after they end the session. Captured at start
// (when the impersonator's role is known) since the active /me identity during
// impersonation is the impersonated user, not the impersonator.
const IMPERSONATOR_RETURN_KEY = "bf_impersonator_return"
// Set when the impersonator is a superuser; gates internal-only UI. The server
// re-checks every internal request.
const IMPERSONATOR_SUPERUSER_KEY = "bf_impersonator_superuser"

// Event emitter for auth token changes
type TokenChangeListener = (token: string | null) => void
const tokenChangeListeners = new Set<TokenChangeListener>()

const emitTokenChange = (token: string | null) => {
  tokenChangeListeners.forEach((listener) => listener(token))
}

/**
 * Get the current auth token from localStorage
 */
export const getAuthToken = (): string | null => {
  if (typeof window === "undefined") return null

  try {
    return localStorage.getItem(AUTH_TOKEN_KEY)
  } catch (error) {
    return null
  }
}

/**
 * Set the auth token in localStorage. Pass `{ silent: true }` to skip notifying
 * token-change listeners — the impersonation flows full-page reload right after,
 * and emitting would re-render the tree with a half-swapped identity (token
 * changed, queries not refetched), crashing components that gate hooks on auth.
 */
export const setAuthToken = (
  token: string | null,
  options?: { silent?: boolean }
): void => {
  if (typeof window === "undefined") return

  try {
    if (token) {
      localStorage.setItem(AUTH_TOKEN_KEY, token)
    } else {
      localStorage.removeItem(AUTH_TOKEN_KEY)
    }

    // Emit token change event (unless explicitly silenced)
    if (!options?.silent) {
      emitTokenChange(token)
    }
  } catch (error) {}
}

/**
 * Remove the auth token from localStorage
 */
export const clearAuthToken = (): void => {
  setAuthToken(null)
}

/**
 * Check if user is authenticated (has a token)
 */
export const isAuthenticated = (): boolean => {
  return getAuthToken() !== null
}

/**
 * Get the stashed impersonator (admin) token, if an impersonation session is
 * active.
 */
export const getImpersonatorToken = (): string | null => {
  if (typeof window === "undefined") return null

  try {
    return localStorage.getItem(IMPERSONATOR_TOKEN_KEY)
  } catch (error) {
    return null
  }
}

/**
 * Whether an admin impersonation session is currently active.
 */
export const isImpersonating = (): boolean => {
  return getImpersonatorToken() !== null
}

/**
 * Begin impersonating: stash the current (admin) token, then switch the active
 * token to the impersonation token. No-op if there is no current token to stash.
 */
export const startImpersonation = (
  impersonationToken: string,
  returnPath?: string,
  impersonatorIsSuperuser = false
): void => {
  if (typeof window === "undefined") return

  try {
    const adminToken = getAuthToken()
    if (adminToken) {
      localStorage.setItem(IMPERSONATOR_TOKEN_KEY, adminToken)
    }
    if (returnPath) {
      localStorage.setItem(IMPERSONATOR_RETURN_KEY, returnPath)
    }
    if (impersonatorIsSuperuser) {
      localStorage.setItem(IMPERSONATOR_SUPERUSER_KEY, "1")
    } else {
      localStorage.removeItem(IMPERSONATOR_SUPERUSER_KEY)
    }
    // Silent: the caller reloads the app immediately, which re-reads the token
    // from localStorage. Skipping the emit avoids a crash-prone intermediate
    // render under the half-swapped identity.
    setAuthToken(impersonationToken, { silent: true })
  } catch (error) {}
}

export const isImpersonatingAsSuperuser = (): boolean => {
  if (!isImpersonating()) return false
  try {
    return localStorage.getItem(IMPERSONATOR_SUPERUSER_KEY) === "1"
  } catch (error) {
    return false
  }
}

/**
 * Where to send the impersonator after they end the session. Defaults to
 * "/loans" (valid for any authenticated user) when nothing was stashed.
 */
export const getImpersonationReturnPath = (): string => {
  if (typeof window === "undefined") return "/loans"

  try {
    return localStorage.getItem(IMPERSONATOR_RETURN_KEY) || "/loans"
  } catch (error) {
    return "/loans"
  }
}

/**
 * End impersonation locally: restore the stashed admin token (if any) as the
 * active token and clear the stash. Returns the restored admin token.
 */
export const stopImpersonationLocal = (): string | null => {
  if (typeof window === "undefined") return null

  try {
    const adminToken = getImpersonatorToken()
    localStorage.removeItem(IMPERSONATOR_TOKEN_KEY)
    localStorage.removeItem(IMPERSONATOR_RETURN_KEY)
    localStorage.removeItem(IMPERSONATOR_SUPERUSER_KEY)
    // Silent: the caller reloads the app immediately, which re-reads the token
    // from localStorage. Skipping the emit avoids a crash-prone intermediate
    // render under the half-swapped identity.
    setAuthToken(adminToken, { silent: true })
    return adminToken
  } catch (error) {
    return null
  }
}

/**
 * Subscribe to auth token changes
 */
export const subscribeToTokenChanges = (
  listener: TokenChangeListener
): (() => void) => {
  tokenChangeListeners.add(listener)

  // Return unsubscribe function
  return () => {
    tokenChangeListeners.delete(listener)
  }
}
